import React, { useCallback, useRef, useState } from 'react';

import {
  Button,
  Caption,
  Heading,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Text,
  toast,
} from '@eluve/components';
import { useAppointmentContext } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';

import { CitationChip } from '../citations/CitationChip';
import {
  BaseCitation,
  CitationDetailsModal,
  getDocumentTypeInfo,
} from '../citations/CitationComponents';

import { FHIRMedicineOrderForm } from './FHIRMedicineOrderForm';
import { FHIRMedication } from './FHIRMedicineRecommendations.types';
import {
  CITATION_SOURCE_LABELS,
  ConflictSeverity,
  FHIRMedicationOrder,
  FHIRMedicationRecommendation,
  FHIRMedicineRecommendationsProps,
  RecommendationCitation,
} from './FHIRMedicineRecommendations.types';
import { RxNormDrug, useRxNormSearch } from './useRxNormSearch';

// Styling helpers for conflict severity
const getConflictBorderClass = (
  hasConflicts: boolean,
  severity?: ConflictSeverity,
): string => {
  if (!hasConflicts) return 'border-border-primary border-dashed';
  if (severity === 'severe')
    return 'border-border-negative-strong border-dashed';
  if (severity === 'moderate') return 'border-border-warning border-dashed';
  return 'border-border-primary border-dashed';
};

const getConflictBoxClasses = (
  severity?: ConflictSeverity,
): { bg: string; text: string } => {
  if (severity === 'severe') {
    return { bg: 'bg-bg-negative-subtle', text: 'text-text-negative' };
  }
  // Moderate and mild use amber styling
  return { bg: 'bg-bg-warning-subtle', text: 'text-text-warning' };
};

// Convert route to adverb form for prescription instructions
const formatRouteAdverb = (route: string): string => {
  const routeAdverbs: Record<string, string> = {
    oral: 'orally',
    topical: 'topically',
    subcutaneous: 'subcutaneously',
    intramuscular: 'intramuscularly',
    intravenous: 'intravenously',
    inhalation: 'by inhalation',
    ophthalmic: 'in the eye',
    otic: 'in the ear',
    nasal: 'nasally',
    rectal: 'rectally',
    transdermal: 'transdermally',
    sublingual: 'sublingually',
  };
  return routeAdverbs[route.toLowerCase()] ?? route;
};

// Adapter function to convert RecommendationCitation to BaseCitation
const recommendationCitationToBaseCitation = (
  citation: RecommendationCitation,
): BaseCitation => ({
  documentTitle: CITATION_SOURCE_LABELS[citation.sourceType],
  citedText: citation.text,
  artifactId: citation.sourceId,
});

// Recommendation card component
interface RecommendationCardProps {
  recommendation: FHIRMedicationRecommendation;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAddOrder: (order: FHIRMedicationOrder) => void;
  tenantId: string | null;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  isExpanded,
  onToggleExpand,
  onAddOrder,
  tenantId,
}) => {
  const { t } = useTranslation();
  const [clickedCitation, setClickedCitation] = useState<BaseCitation | null>(
    null,
  );

  const handleSubmit = (order: FHIRMedicationOrder) => {
    onAddOrder(order);
    onToggleExpand();
  };

  const hasConflicts = recommendation.hasConflicts ?? false;
  const borderClass = getConflictBorderClass(
    hasConflicts,
    recommendation.conflictSeverity,
  );
  const conflictStyles = getConflictBoxClasses(recommendation.conflictSeverity);

  return (
    <div className={`w-full rounded-lg border bg-bg-primary ${borderClass}`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full rounded-lg p-2 text-left transition-colors hover:bg-bg-secondary"
      >
        <Stack gap={1}>
          {/* Main row: Plus icon + medication info */}
          <Stack direction="horizontal" align="center" gap={2}>
            <Icon name="Plus" className="shrink-0 text-text-tertiary" />
            <Text inline className="shrink-0 capitalize">
              {recommendation.medication.name}
            </Text>
            {(recommendation.medication.strength ||
              recommendation.medication.form) && (
              <Text inline color="tertiary" className="shrink-0">
                {[
                  recommendation.medication.strength,
                  recommendation.medication.form,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
            )}
            {recommendation.reasonCode && (
              <>
                <Text color="tertiary" className="shrink-0">
                  ·
                </Text>
                <Text color="secondary" className="truncate">
                  {recommendation.reasonCode}
                </Text>
              </>
            )}
          </Stack>

          {/* Conflict warning row */}
          {hasConflicts && recommendation.conflictDescription && (
            <div
              className={`ml-5 flex items-start gap-2 rounded-md px-2 py-1.5 ${conflictStyles.bg}`}
            >
              <Icon
                name="AlertTriangle"
                className={`mt-0.5 shrink-0 ${conflictStyles.text}`}
              />
              <Text className={conflictStyles.text}>
                {recommendation.conflictDescription}
              </Text>
            </div>
          )}

          {/* Citations row - below warnings */}
          {recommendation.citations.length > 0 && (
            <Stack
              className="pl-5"
              direction="horizontal"
              align="center"
              gap={1.5}
              fullWidth={false}
            >
              <Text size="xs" color="tertiary">
                {t('appointmentTools.citations.sources')}
              </Text>
              {recommendation.citations.map((citation) => {
                const baseCitation =
                  recommendationCitationToBaseCitation(citation);
                const { label, icon } = getDocumentTypeInfo(
                  baseCitation.documentTitle,
                );

                return (
                  <CitationChip
                    key={citation.id}
                    label={label}
                    icon={icon}
                    size="xs"
                    onClick={() => setClickedCitation(baseCitation)}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      </button>

      {isExpanded && (
        <div className="border-t border-border-primary p-3">
          <FHIRMedicineOrderForm
            medication={recommendation.medication}
            suggestedDosage={recommendation.suggestedDosage}
            onSubmit={handleSubmit}
            onCancel={onToggleExpand}
          />
        </div>
      )}

      {clickedCitation && tenantId && (
        <CitationDetailsModal
          citation={clickedCitation}
          isOpen={true}
          onClose={() => setClickedCitation(null)}
          tenantId={tenantId}
        />
      )}
    </div>
  );
};

// Main component
export const FHIRMedicineRecommendations: React.FC<
  FHIRMedicineRecommendationsProps
> = ({
  isReadonly = false,
  recommendations = [],
  orders = [],
  onOrderSubmit,
  onOrderRemove,
  onOrderUpdate,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useAppointmentContext();
  const [expandedRecommendationId, setExpandedRecommendationId] = useState<
    string | null
  >(null);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedSearchMedication, setSelectedSearchMedication] =
    useState<FHIRMedication | null>(null);
  const [isSendingToPharmacy, setIsSendingToPharmacy] = useState(false);
  const [sentToPharmacyAt, setSentToPharmacyAt] = useState<Date | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // RxNorm search integration
  const { results: searchResults, isLoading: isSearching } =
    useRxNormSearch(searchTerm);

  // Convert RxNorm result to FHIRMedication
  const rxNormToFHIRMedication = useCallback(
    (drug: RxNormDrug): FHIRMedication => ({
      name: drug.name,
      code: drug.rxcui,
    }),
    [],
  );

  // Handle selecting a search result
  const handleSelectSearchResult = useCallback(
    (drug: RxNormDrug) => {
      setSelectedSearchMedication(rxNormToFHIRMedication(drug));
      setShowSearchResults(false);
      setSearchTerm('');
      setExpandedRecommendationId(null);
      setEditingOrderId(null);
    },
    [rxNormToFHIRMedication],
  );

  // Handle canceling the search medication form
  const handleCancelSearchMedication = useCallback(() => {
    setSelectedSearchMedication(null);
  }, []);

  // Filter out recommendations that have already been ordered
  const orderedMedicationNames = new Set(
    orders.map((o) => o.medication.name.toLowerCase()),
  );
  const filteredRecommendations = recommendations.filter(
    (r) => !orderedMedicationNames.has(r.medication.name.toLowerCase()),
  );

  const handleToggleExpand = useCallback((recommendationId: string) => {
    setExpandedRecommendationId((prev) =>
      prev === recommendationId ? null : recommendationId,
    );
    setEditingOrderId(null); // Close any editing order form
  }, []);

  const handleAddOrder = useCallback(
    (order: FHIRMedicationOrder) => {
      onOrderSubmit?.(order);
      setExpandedRecommendationId(null);
    },
    [onOrderSubmit],
  );

  const handleRemoveOrder = useCallback(
    (orderId: string) => {
      onOrderRemove?.(orderId);
    },
    [onOrderRemove],
  );

  const handleEditOrder = useCallback((orderId: string) => {
    setEditingOrderId((prev) => (prev === orderId ? null : orderId));
    setExpandedRecommendationId(null); // Close any open recommendation form
  }, []);

  const handleUpdateOrder = useCallback(
    (updatedOrder: FHIRMedicationOrder) => {
      onOrderUpdate?.(updatedOrder);
      setEditingOrderId(null);
    },
    [onOrderUpdate],
  );

  const handleSendToPharmacy = useCallback(async () => {
    setIsSendingToPharmacy(true);
    await new Promise((resolve) => setTimeout(resolve, 3000));
    setIsSendingToPharmacy(false);
    setSentToPharmacyAt(new Date());
    toast.success(t('appointmentTools.medicine.ordersSentSuccess'));
  }, [t]);

  const handleViewPdf = useCallback(async () => {
    if (orders.length === 0) return;

    setIsGeneratingPdf(true);
    try {
      // Dynamic imports with webpackIgnore to completely exclude react-pdf from extension bundle.
      // This is necessary because yoga-layout (a react-pdf dependency) has TypeScript source files
      // that the extension webpack config can't process.
      const [{ pdf }, { MedicationOrdersPDF }] = await Promise.all([
        import(/* webpackIgnore: true */ '@react-pdf/renderer'),
        import(/* webpackIgnore: true */ './MedicationOrdersPDF'),
      ]);
      const pdfDocument = (
        <MedicationOrdersPDF orders={orders} generatedAt={new Date()} />
      );
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error(t('appointmentTools.common.generatePdfFailed'));
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [orders, t]);

  // Empty state check
  if (
    isReadonly &&
    orders.length === 0 &&
    filteredRecommendations.length === 0
  ) {
    return null;
  }

  return (
    <Stack gap={4}>
      <Heading size="xl" weight="semibold">
        {t('appointmentTools.medicine.medicationOrders')}
      </Heading>
      <Stack gap={6} className="rounded-lg border border-border-primary p-6">
        {/* Search bar */}
        {!isReadonly && (
          <div ref={searchContainerRef} className="relative w-full">
            <div className="relative w-full">
              <Icon
                name="Search"
                className="absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary"
              />
              <Input
                disabled={isReadonly}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                onBlur={() => {
                  // Delay hiding to allow click on results
                  setTimeout(() => setShowSearchResults(false), 200);
                }}
                placeholder={t('appointmentTools.medicine.searchMedications')}
                className="w-full pl-10"
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2">
                  <LoadingIcon />
                </div>
              )}
            </div>

            {/* Search results dropdown */}
            {showSearchResults && searchTerm.length >= 2 && (
              <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg">
                {isSearching && searchResults.length === 0 && (
                  <Stack
                    className="p-4"
                    direction="horizontal"
                    align="center"
                    justify="center"
                  >
                    <LoadingIcon size="xl" />
                    <Text size="md" color="tertiary" className="ml-2">
                      {t('appointmentTools.common.searching')}
                    </Text>
                  </Stack>
                )}
                {!isSearching && searchResults.length === 0 && (
                  <div className="p-4 text-center">
                    <Text size="md" color="tertiary">
                      {t('appointmentTools.medicine.noMedicationsFound', {
                        query: searchTerm,
                      })}
                    </Text>
                  </div>
                )}
                {searchResults.length > 0 && (
                  <Stack>
                    {searchResults.map((drug) => (
                      <button
                        key={drug.rxcui}
                        type="button"
                        className="w-full px-3 py-2 text-left transition-colors hover:bg-bg-secondary"
                        onClick={() => handleSelectSearchResult(drug)}
                      >
                        <Stack>
                          <Text inline className="capitalize">
                            {drug.name}
                          </Text>
                          {drug.tty && (
                            <Text inline color="tertiary">
                              RxCUI: {drug.rxcui} · {drug.tty}
                            </Text>
                          )}
                        </Stack>
                      </button>
                    ))}
                  </Stack>
                )}
              </div>
            )}
          </div>
        )}

        {/* Order form for selected search medication */}
        {selectedSearchMedication && !isReadonly && (
          <div className="w-full rounded-lg border border-border-primary bg-bg-primary p-3">
            <Stack gap={3}>
              <Stack
                gap={2}
                direction="horizontal"
                align="center"
                justify="between"
              >
                <Text inline weight="semibold" className="capitalize">
                  {t('appointmentTools.medicine.newOrder', {
                    name: selectedSearchMedication.name,
                  })}
                </Text>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelSearchMedication}
                >
                  <Icon name="X" size="auto" />
                </Button>
              </Stack>
              <FHIRMedicineOrderForm
                medication={selectedSearchMedication}
                onSubmit={(order) => {
                  handleAddOrder(order);
                  setSelectedSearchMedication(null);
                }}
                onCancel={handleCancelSearchMedication}
              />
            </Stack>
          </div>
        )}

        {/* Current Orders */}
        {orders.length > 0 && (
          <Stack gap={3}>
            <Caption size="2xs" color="tertiary">
              {t('appointmentTools.medicine.currentOrders')}
            </Caption>
            <Stack gap={2}>
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="w-full rounded-lg border border-border-primary bg-bg-primary p-2"
                >
                  <Stack
                    direction="horizontal"
                    align="start"
                    justify="between"
                    gap={3}
                  >
                    <Stack className="min-w-0 flex-1" gap={0.5} align="stretch">
                      <Stack
                        direction="horizontal"
                        align="center"
                        gap={2}
                        fullWidth={false}
                      >
                        <Text className="capitalize">
                          {order.medication.name}
                        </Text>
                        {(order.medication.strength ||
                          order.medication.form) && (
                          <Text color="tertiary">
                            {[order.medication.strength, order.medication.form]
                              .filter(Boolean)
                              .join(' ')}
                          </Text>
                        )}
                      </Stack>
                      <Text color="secondary">
                        {t('appointmentTools.medicine.takeInstruction', {
                          dose: order.dose,
                          route: formatRouteAdverb(order.route),
                          frequency: order.frequency,
                        })}
                      </Text>
                      {order.instructions && (
                        <Text color="tertiary" className="truncate">
                          {order.instructions}
                        </Text>
                      )}
                    </Stack>
                    {!isReadonly && (
                      <Stack
                        className="shrink-0"
                        direction="horizontal"
                        gap={0}
                        align="stretch"
                        fullWidth={false}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditOrder(order.id)}
                        >
                          <Icon name="Pencil" size="auto" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOrder(order.id)}
                        >
                          <Icon name="X" size="auto" />
                        </Button>
                      </Stack>
                    )}
                  </Stack>

                  {editingOrderId === order.id && (
                    <div className="mt-2 border-t border-border-primary pt-2">
                      <FHIRMedicineOrderForm
                        medication={order.medication}
                        existingOrder={order}
                        onSubmit={handleUpdateOrder}
                        onCancel={() => setEditingOrderId(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </Stack>

            {!isReadonly && (
              <Stack
                direction="horizontal"
                align="center"
                justify="end"
                gap={3}
                fullWidth={false}
              >
                <Button
                  variant="outline"
                  size="sm"
                  loading={isGeneratingPdf}
                  onClick={handleViewPdf}
                >
                  <Icon name="FileText" size="auto" />
                  {t('appointmentTools.medicine.viewPdf')}
                </Button>
                <Button
                  size="sm"
                  loading={isSendingToPharmacy}
                  disabled={Boolean(sentToPharmacyAt)}
                  onClick={handleSendToPharmacy}
                >
                  <Icon name="Send" size="auto" />
                  {t('appointmentTools.medicine.sendToPharmacy')}
                </Button>
                {sentToPharmacyAt && (
                  <Text color="tertiary">
                    {t('appointmentTools.medicine.orderSentAt', {
                      time: sentToPharmacyAt.toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      }),
                    })}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {/* AI Recommendations */}
        {filteredRecommendations.length > 0 && !isReadonly && (
          <Stack gap={3}>
            <Caption size="2xs" color="tertiary">
              {t('appointmentTools.medicine.quickActions')}
            </Caption>
            <Stack gap={2}>
              {filteredRecommendations.map((recommendation) => (
                <RecommendationCard
                  key={recommendation.id}
                  recommendation={recommendation}
                  isExpanded={expandedRecommendationId === recommendation.id}
                  onToggleExpand={() => handleToggleExpand(recommendation.id)}
                  onAddOrder={handleAddOrder}
                  tenantId={tenantId}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {/* Empty state for recommendations */}
        {orders.length === 0 &&
          filteredRecommendations.length === 0 &&
          !isReadonly && (
            <div className="rounded-lg border border-dashed border-border-primary p-6 text-center">
              <Text size="md" color="tertiary">
                {t('appointmentTools.medicine.emptyOrders')}
              </Text>
            </div>
          )}
      </Stack>
    </Stack>
  );
};
